import { useContext } from "react";

import Cookie from "js-cookie";

import { AccountTypeEnum, LanguageEnum } from "graphql/types";
import { AuthContext } from "providers/Authentication";
import { IntlContext } from "providers/i18n";
import { getParsedJSON } from "utils";

type CommonRoutes =
  | "HOME"
  | "PRIVACY"
  | "TERMS"
  | "FREELANCER"
  | "GATE_KEEPER"
  | "BANK_ID_VERIFICATION"
  | "CRIIPTO_CALLBACK"
  | "REFERRER"
  | "BENEFITS";
type TestRoutes = "ERROR_PAGE" | "COMPONENTS";
type LoginRoutes = "LOGIN" | "MAGIC_LINK_LOGIN" | "MAGIC_LINK_VERIFICATION";
type AccountRoutes =
  | "DASHBOARD"
  | "SCHEDULED_DELETION"
  | "SETTINGS"
  | "SETTINGS_INFO"
  | "SETTINGS_AUTH"
  | "SETTINGS_TEAM"
  | "SETTINGS_TEAM_MEMBERS"
  | "SETTINGS_PAYOUT";
type InvoicemoreRoutes =
  | "PUBLIC_INVOICE"
  | "PAYOUT_SERVICE"
  | "PAYOUT_SERVICE_INVOICES"
  | "PAYOUT_SERVICE_DRAFTS"
  | "PAYOUT_SERVICE_CREDIT_NOTES"
  | "PAYOUT_SERVICE_BY_CLIENT"
  | "PAYOUT_SERVICE_SALARY_SLIPS"
  | "PAYOUT_SERVICE_SALARY_REPORTS"
  | "INVOICE"
  | "INVOICE_CREATION"
  | "INVOICE_DUPLICATION"
  | "CREDIT_NOTE"
  | "CREDIT_NOTE_CREATION"
  | "FREELANCE_PROFILE_WIZARD";
type PayoutmoreRoutes =
  | "PURCHASES"
  | "PURCHASES_ALL"
  | "PURCHASES_REFUNDS"
  | "PURCHASES_BY_SUPPLIER";

export interface RoutesByModule {
  common: CommonRoutes;
  test: TestRoutes;
  login: LoginRoutes;
  account: AccountRoutes;
  invoicemore: InvoicemoreRoutes;
  payoutmore: PayoutmoreRoutes;
}
export type Module =
  | "common"
  | "test"
  | "login"
  | "account"
  | "invoicemore"
  | "payoutmore";

enum RouteAuthEnum {
  All = "ALL",
  LoggedOut = "LOGGED_OUT",
  LoggedIn = "LOGGED_IN",
}

const routesByModule: {
  [T in Module]: {
    [K in RoutesByModule[T]]: (
      arg1?: string,
      arg2?: string,
      arg3?: string
    ) => { path: string; auth: RouteAuthEnum };
  };
} = {
  common: {
    HOME: () => ({ path: "home", auth: RouteAuthEnum.All }),
    PRIVACY: () => ({ path: "privacy", auth: RouteAuthEnum.All }),
    TERMS: () => ({ path: "terms", auth: RouteAuthEnum.All }),
    FREELANCER: () => ({ path: "freelancer", auth: RouteAuthEnum.All }),
    CRIIPTO_CALLBACK: () => ({
      path: "criipto-callback",
      auth: RouteAuthEnum.All,
    }),
    REFERRER: (referrer) => ({
      path: `referrer/${referrer}`,
      auth: RouteAuthEnum.All,
    }),
    GATE_KEEPER: () => ({
      path: "action-required",
      auth: RouteAuthEnum.LoggedIn,
    }),
    BANK_ID_VERIFICATION: () => ({
      path: "bank-id-verification",
      auth: RouteAuthEnum.LoggedIn,
    }),
    BENEFITS: () => ({
      path: "benefits",
      auth: RouteAuthEnum.LoggedIn,
    }),
  },
  test: {
    ERROR_PAGE: () => ({
      path: "error-page",
      auth: RouteAuthEnum.All,
    }),
    COMPONENTS: () => ({
      path: "component-library",
      auth: RouteAuthEnum.All,
    }),
  },
  login: {
    LOGIN: () => ({
      path: "existing-user",
      auth: RouteAuthEnum.LoggedOut,
    }),
    MAGIC_LINK_LOGIN: () => ({
      path: "magic-link",
      auth: RouteAuthEnum.LoggedOut,
    }),
    MAGIC_LINK_VERIFICATION: () => ({
      path: "magic-link/verification",
      auth: RouteAuthEnum.All,
    }),
  },
  account: {
    DASHBOARD: () => ({ path: "dashboard", auth: RouteAuthEnum.LoggedIn }),
    SCHEDULED_DELETION: () => ({
      path: "scheduled-deletion",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS: () => ({
      path: "settings",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS_INFO: () => ({
      path: "settings/account-info",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS_AUTH: () => ({
      path: "settings/authentication",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS_TEAM: () => ({
      path: "settings/team",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS_TEAM_MEMBERS: () => ({
      path: "settings/team/members",
      auth: RouteAuthEnum.LoggedIn,
    }),
    SETTINGS_PAYOUT: () => ({
      path: "settings/payout-settings",
      auth: RouteAuthEnum.LoggedIn,
    }),
  },
  invoicemore: {
    PUBLIC_INVOICE: (id) => ({
      path: `public-invoice/${id}`,
      auth: RouteAuthEnum.All,
    }),
    PAYOUT_SERVICE: () => ({
      path: "payout-service",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_INVOICES: () => ({
      path: "payout-service/invoices",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_DRAFTS: () => ({
      path: "payout-service/drafts",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_CREDIT_NOTES: () => ({
      path: "payout-service/credit-notes",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_BY_CLIENT: () => ({
      path: "payout-service/by-client",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_SALARY_SLIPS: () => ({
      path: "payout-service/salary-slips",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PAYOUT_SERVICE_SALARY_REPORTS: () => ({
      path: "payout-service/salary-reports",
      auth: RouteAuthEnum.LoggedIn,
    }),
    INVOICE: (id) => ({
      path: `invoice/${id}`,
      auth: RouteAuthEnum.LoggedIn,
    }),
    INVOICE_CREATION: (id) => ({
      path: id ? `invoice-creation/${id}` : "invoice-creation",
      auth: RouteAuthEnum.LoggedIn,
    }),
    INVOICE_DUPLICATION: (id) => ({
      path: `invoice-duplication/${id}`,
      auth: RouteAuthEnum.LoggedIn,
    }),
    CREDIT_NOTE: (id) => ({
      path: `credit-note/${id}`,
      auth: RouteAuthEnum.LoggedIn,
    }),
    CREDIT_NOTE_CREATION: (id) => ({
      path: `credit-note-creation/${id}`,
      auth: RouteAuthEnum.LoggedIn,
    }),
    FREELANCE_PROFILE_WIZARD: (id) => ({
      path: id ? `freelance-profile-wizard/${id}` : "freelance-profile-wizard",
      auth: RouteAuthEnum.LoggedIn,
    }),
  },
  payoutmore: {
    PURCHASES: () => ({
      path: "purchases",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PURCHASES_ALL: () => ({
      path: "purchases/all",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PURCHASES_REFUNDS: () => ({
      path: "purchases/refunds",
      auth: RouteAuthEnum.LoggedIn,
    }),
    PURCHASES_BY_SUPPLIER: () => ({
      path: "purchases/by-supplier",
      auth: RouteAuthEnum.LoggedIn,
    }),
  },
};

type RouteGetter<R> = <T extends Module>(
  module: T,
  routeName: RoutesByModule[T],
  arg1?: string,
  arg2?: string,
  arg3?: string
) => R;
export const getPath: RouteGetter<{ path: string; auth: RouteAuthEnum }> = (
  module,
  routeName,
  arg1,
  arg2,
  arg3
): { path: string; auth: RouteAuthEnum } =>
  routesByModule[module][routeName](arg1, arg2, arg3);
export const getRoutePath: RouteGetter<string> = (...props) =>
  getPath(...props).path;

export const unspecifiedTypeString = "t";
export const unspecifiedSlugString = "a";

export interface GetRoutePathProps<T extends Module> {
  module: T;
  routeName: RoutesByModule[T];
  arg1?: string;
  arg2?: string;
  arg3?: string;
}
interface RouteOptions {
  isGeneric?: boolean;
  locale?: LanguageEnum;
  account?: {
    slug: string;
    type: AccountTypeEnum;
  };
}
export const useRoutePaths = () => {
  const { currentLocale } = useContext(IntlContext);
  const { session } = useContext(AuthContext);

  return <T extends Module>(
    route: GetRoutePathProps<T>,
    options: RouteOptions = {}
  ) => {
    const locale = options.locale ?? currentLocale;
    const module = route.module;
    const type = options.isGeneric
      ? unspecifiedTypeString
      : (
          options.account?.type ??
          session?.account.accountType ??
          unspecifiedTypeString
        ).toLowerCase();
    const slug = options.isGeneric
      ? unspecifiedSlugString
      : (options.account?.slug ??
        session?.account.slug ??
        unspecifiedSlugString);

    const { auth, path } = getPath(
      module,
      route.routeName,
      route.arg1,
      route.arg2,
      route.arg3
    );

    const components = (() => {
      switch (auth) {
        case RouteAuthEnum.LoggedIn:
          return [getLocalePrefix(locale), type, slug, module, path];

        case RouteAuthEnum.LoggedOut:
          return [getLocalePrefix(locale), module, path];

        default:
          return !options.isGeneric &&
            (options.account?.slug ?? session?.account.id)
            ? [getLocalePrefix(locale), type, slug, module, path]
            : [getLocalePrefix(locale), module, path];
      }
    })();

    return "/v2" + components.join("/");
  };
};

export const getLocalePrefix = (locale: LanguageEnum): string => {
  const localePrefix =
    Object.keys(LanguageEnum).find(
      // TODO: Typecasting should not be necessary here
      (key) => LanguageEnum[key as keyof typeof LanguageEnum] === locale
    ) ?? LanguageEnum.En;

  return `/${localePrefix.toLowerCase()}`;
};

export const useDefaultRoutePath = (): string => {
  const { isLoggedIn, session } = useContext(AuthContext);
  const getRoutePath = useRoutePaths();

  if (!isLoggedIn) return getRoutePath({ module: "common", routeName: "HOME" });
  // TODO: Fix case when session is not available when logged in
  if (!session)
    return getRoutePath({ module: "account", routeName: "DASHBOARD" });

  const lastvisit = getLastVisitFromCookie(session.user.id);
  if (lastvisit) {
    const routeName = getDefaultRouteNameFromModule(lastvisit.module);
    if (routeName) return getRoutePath({ routeName, module: lastvisit.module });
  }

  // Personal account defaults:
  if (session.account.accountType === AccountTypeEnum.Personal) {
    if (session.hasFreelanceProfiles === true)
      return getRoutePath({
        module: "invoicemore",
        routeName: "INVOICE_CREATION",
      });

    return getRoutePath({ module: "account", routeName: "SETTINGS_PAYOUT" });
  }

  // Team account defaults:
  return getRoutePath({ module: "invoicemore", routeName: "PAYOUT_SERVICE" });
};

const getLastVisitFromCookie = (
  userId: string
): {
  slug: string;
  module: Module;
} | null => {
  const cookie: unknown = Cookie.get("mm-module");
  const parsedCookie = getParsedJSON(cookie) as Record<
    string,
    { slug?: unknown; module?: unknown }
  > | null;
  const unverifiedVisit = parsedCookie?.[userId];
  if (!unverifiedVisit) return null;

  const hasSlug =
    Object.prototype.hasOwnProperty.call(unverifiedVisit, "slug") &&
    typeof unverifiedVisit.slug === "string";

  const hasModule =
    Object.prototype.hasOwnProperty.call(unverifiedVisit, "module") &&
    typeof unverifiedVisit.module === "string";

  if (hasSlug && hasModule) {
    const verifiedVisit = unverifiedVisit as { slug: string; module: Module };

    return verifiedVisit;
  }

  return null;
};

export const getDefaultRouteNameFromModule = (
  module: Module
): RoutesByModule[Module] | null => {
  // prettier-ignore
  switch (module) {
    case "test": return null;
    case "common": return "HOME";
    case "login": return "LOGIN";
    case "account": return "SETTINGS_INFO";
    case "payoutmore": return "PURCHASES";
    case "invoicemore": return "PAYOUT_SERVICE";    

    default: return ((_module: never) => null)(module);
  }
};

export const checkIsRoutePath = <T extends Module>(
  pathname: string,
  { module, routeName, arg1 = "", arg2 = "", arg3 = "" }: GetRoutePathProps<T>
) =>
  pathname.includes(
    getRoutePath(module, routeName, arg1, arg2, arg3).replace(/\/*$/, "")
  );
